/**
 * FluxAppsWidget Component
 *
 * Compact widget showing Flux deployed apps status for the Portfolio Dashboard
 * - Shows app count and total instances
 * - Clickable to navigate to FluxNode apps tab
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { fluxNodeAPI, type FluxDeployedAppsSummary } from '../../services/fluxNodeApi';

const APPS_OWNER_STORAGE_KEY = 'fluxnode_apps_owner';

interface FluxAppsWidgetProps {
  onNavigate?: (pageId: string) => void;
}

export function FluxAppsWidget({ onNavigate }: FluxAppsWidgetProps) {
  const { t } = useTranslation();
  const [owner, setOwner] = useState<string | null>(null);
  const [appsData, setAppsData] = useState<FluxDeployedAppsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load owner from localStorage and fetch apps data
  useEffect(() => {
    const savedOwner = localStorage.getItem(APPS_OWNER_STORAGE_KEY);
    if (savedOwner) {
      setOwner(savedOwner);
      fetchAppsData(savedOwner);
    }
  }, []);

  const fetchAppsData = async (ownerAddress: string) => {
    setIsLoading(true);
    try {
      const result = await fluxNodeAPI.getDeployedAppsByOwner(ownerAddress);
      if (result) {
        setAppsData(result);
      }
    } catch (error) {
      console.error('Error fetching apps data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (onNavigate) {
      // Navigate to fluxnode-monitor page, the #apps hash will be handled by URL sync
      onNavigate('fluxnode-monitor');
      // Set the hash after a short delay to allow navigation to complete
      setTimeout(() => {
        window.location.hash = 'apps';
      }, 100);
    }
  };

  const totalApps = appsData?.totalApps || 0;
  const totalInstances = appsData?.totalInstances || 0;
  const isConfigured = !!owner;

  // Show skeleton/placeholder if not configured
  if (!isConfigured) {
    return (
      <div
        onClick={handleClick}
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.06), rgba(37, 99, 235, 0.04))',
          borderRadius: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px dashed rgba(59, 130, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '180px',
          opacity: 0.7,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
        }}
        title={t('fluxApps.widget.clickToConfigure', 'Cliquer pour configurer Flux Apps')}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'rgba(59, 130, 246, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icons.Box size={18} color="rgba(59, 130, 246, 0.5)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', opacity: 0.6 }}>
              Flux Apps
            </span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted)', opacity: 0.6 }}>
            {t('fluxApps.widget.clickToConfigure', 'Cliquer pour configurer')}
          </span>
        </div>
        <Icons.Plus size={16} style={{ color: 'rgba(59, 130, 246, 0.5)', flexShrink: 0 }} />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(37, 99, 235, 0.08))',
        borderRadius: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '180px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
      }}
      title={t('fluxNode.widget.clickToOpenApps', 'Cliquer pour voir les applications')}
    >
      {/* Icon */}
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icons.Package size={18} color="white" />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
            Flux Apps
          </span>
          {/* Status indicator */}
          {isLoading ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#6b7280',
                animation: 'pulse 1s infinite',
              }}
            />
          ) : totalApps > 0 ? (
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 8px #10b981',
              }}
            />
          ) : null}
        </div>

        {/* Apps info */}
        {isLoading ? (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            Chargement...
          </span>
        ) : totalApps > 0 ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icons.Box size={10} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {totalApps} app{totalApps > 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icons.Server size={10} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {totalInstances} inst.
              </span>
            </div>
          </div>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('fluxNode.widget.noApps', 'Aucune app')}
          </span>
        )}
      </div>

      {/* Arrow */}
      <Icons.ChevronRight size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
